import moment from 'moment';
import qzTray from 'qz-tray';
import {
    DEFAULT_RECEIPT_PAPER_PRESET,
    normalizeReceiptPaperWidthMm,
} from '@/lib/receiptPaper';

const qz = qzTray?.default || qzTray;

export const DEFAULT_RECEIPT_PRINT_METHOD = 'browser';
export const RECEIPT_PRINT_METHOD_OPTIONS = [
    {
        value: DEFAULT_RECEIPT_PRINT_METHOD,
        label: 'Browser print dialog',
    },
    {
        value: 'qz',
        label: 'Direct thermal printer (QZ Tray)',
    },
];

const ESC = '\x1B';
const GS = '\x1D';

const COMMANDS = {
    init: `${ESC}@`,
    alignLeft: `${ESC}a\x00`,
    alignCenter: `${ESC}a\x01`,
    boldOn: `${ESC}E\x01`,
    boldOff: `${ESC}E\x00`,
    normalSize: `${GS}!\x00`,
    largeSize: `${GS}!\x11`,
    cut: `${GS}V\x00`,
};

const RECEIPT_PRINTER_NAME_HINTS = [
    /epson/i,
    /tm[-\s]?/i,
    /receipt/i,
    /thermal/i,
    /\bpos\b/i,
    /bixolon/i,
    /star/i,
    /xp[-\s]?/i,
    /\b80mm\b/i,
    /\b58mm\b/i,
    /kitchen/i,
];

const PAYMENT_STATUS_LABELS = {
    1: 'Unpaid',
    2: 'Paid',
    3: 'Failed',
    4: 'Waiting For Approval',
};

let qzSecurityConfigured = false;

const sanitizeText = (value) =>
    String(value ?? '')
        .replace(/\r/g, '')
        .replace(/\t/g, ' ')
        .replace(/[^\S\n]+/g, ' ')
        .split('\0')
        .join('')
        .trim();

const looksLikeReceiptPrinter = (printerName) => {
    const normalizedName = sanitizeText(printerName);
    if (!normalizedName) {
        return false;
    }

    return RECEIPT_PRINTER_NAME_HINTS.some((pattern) => pattern.test(normalizedName));
};

const repeatChar = (char, count) => char.repeat(Math.max(0, count));

const getCharsPerLine = (generalSetting = {}) => {
    const widthMm = normalizeReceiptPaperWidthMm(
        generalSetting?.receipt_paper_width_mm,
        generalSetting?.receipt_paper_preset || DEFAULT_RECEIPT_PAPER_PRESET
    );

    return widthMm >= 72 ? 48 : 32;
};

const padRight = (value, width) => {
    const text = String(value ?? '');
    if (text.length >= width) {
        return text.slice(0, width);
    }
    return `${text}${repeatChar(' ', width - text.length)}`;
};

const padLeft = (value, width) => {
    const text = String(value ?? '');
    if (text.length >= width) {
        return text.slice(text.length - width);
    }
    return `${repeatChar(' ', width - text.length)}${text}`;
};

const wrapText = (value, width) => {
    const text = sanitizeText(value);
    if (!text) {
        return [];
    }

    const sourceLines = text.split('\n');
    const wrappedLines = [];

    sourceLines.forEach((line) => {
        const words = line.split(' ').filter(Boolean);
        if (!words.length) {
            wrappedLines.push('');
            return;
        }

        let currentLine = '';

        words.forEach((word) => {
            if (word.length > width) {
                if (currentLine) {
                    wrappedLines.push(currentLine);
                    currentLine = '';
                }
                for (let index = 0; index < word.length; index += width) {
                    wrappedLines.push(word.slice(index, index + width));
                }
                return;
            }

            const candidate = currentLine ? `${currentLine} ${word}` : word;
            if (candidate.length <= width) {
                currentLine = candidate;
            } else {
                wrappedLines.push(currentLine);
                currentLine = word;
            }
        });

        if (currentLine) {
            wrappedLines.push(currentLine);
        }
    });

    return wrappedLines;
};

const formatCurrency = (price, generalSetting = {}) => {
    const symbol = String(
        generalSetting?.site_default_currency_symbol ??
            generalSetting?.site_default_currency?.symbol ??
            generalSetting?.currency?.symbol ??
            '$'
    ).trim();

    const decimalsRaw = Number(generalSetting?.site_digit_after_decimal_point);
    const decimals = Number.isInteger(decimalsRaw) ? decimalsRaw : 2;
    const position = String(generalSetting?.site_currency_position || '1');
    const numeric = Number(price ?? 0);
    const formatted = Number.isFinite(numeric)
        ? numeric.toFixed(decimals)
        : Number(0).toFixed(decimals);

    return position === '2' ? `${formatted} ${symbol}` : `${symbol} ${formatted}`;
};

const parseSerializedValue = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value === 'object') {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const getItemVariationsText = (itemVariations) => {
    const parsed = parseSerializedValue(itemVariations);
    if (!parsed?._ids?.length) {
        return [];
    }

    return parsed._ids.map((_, index) => {
        const attribute = sanitizeText(parsed?.attribute_name?.[index]);
        const name = sanitizeText(parsed?.name?.[index]);
        return attribute && name ? `${attribute}: ${name}` : name;
    }).filter(Boolean);
};

const getItemExtrasText = (itemExtras) => {
    const parsed = parseSerializedValue(itemExtras);
    if (!parsed?._ids?.length) {
        return [];
    }

    return parsed._ids
        .map((_, index) => sanitizeText(parsed?.name?.[index]))
        .filter(Boolean);
};

const getItemName = (orderData, cart, index) => {
    const items = Array.isArray(orderData?.items) ? orderData.items : [];
    const matchedItem = items.find(
        (item) => String(item?._id ?? '') === String(cart?.item_id ?? '')
    );

    return (
        sanitizeText(matchedItem?.name) ||
        sanitizeText(items?.[index]?.name) ||
        sanitizeText(cart?.item_name) ||
        'Unknown Item'
    );
};

const getServedBy = (orderData, userData) =>
    sanitizeText(
        orderData?.waiter?.name ||
            orderData?.waiter_name ||
            userData?.name ||
            userData?.email ||
            'N/A'
    );

const pushWrappedBlock = (buffer, value, width, prefix = '') => {
    wrapText(value, Math.max(8, width - prefix.length)).forEach((line) => {
        buffer.push(`${prefix}${line}\n`);
    });
};

const pushCenteredBlock = (buffer, value, width) => {
    wrapText(value, width).forEach((line) => {
        buffer.push(`${line}\n`);
    });
};

const buildDivider = (width) => `${repeatChar('-', width)}\n`;

const buildKeyValueLine = (label, value, width) => {
    const safeValue = sanitizeText(value);
    if (!safeValue) {
        return `${sanitizeText(label)}\n`;
    }

    const rightWidth = Math.min(Math.max(8, safeValue.length), Math.max(8, Math.floor(width * 0.45)));
    const leftWidth = Math.max(8, width - rightWidth - 1);
    const wrappedLabel = wrapText(label, leftWidth);

    if (!wrappedLabel.length) {
        return `${padLeft(safeValue, width)}\n`;
    }

    return wrappedLabel
        .map((line, index) =>
            index === 0
                ? `${padRight(line, leftWidth)} ${padLeft(safeValue, rightWidth)}\n`
                : `${line}\n`
        )
        .join('');
};

const buildItemRows = ({ orderData, generalSetting, receiptType, width }) => {
    const qtyWidth = width >= 48 ? 4 : 3;
    const priceWidth = receiptType === 'customer' ? (width >= 48 ? 12 : 9) : 0;
    const descWidth = Math.max(
        8,
        width - qtyWidth - (receiptType === 'customer' ? priceWidth + 1 : 1)
    );

    const rows = [];
    const items = Array.isArray(orderData?.order_items) ? orderData.order_items : [];

    if (receiptType === 'customer') {
        rows.push(
            `${padRight('Qty', qtyWidth)}${padRight('Item Description', descWidth)} ${padLeft(
                'Price',
                priceWidth
            )}\n`
        );
    } else {
        rows.push(`${padRight('Qty', qtyWidth)}${padRight('Item Description', descWidth)}\n`);
    }

    rows.push(buildDivider(width));

    items.forEach((cart, index) => {
        const itemName = getItemName(orderData, cart, index);
        const descriptionLines = wrapText(itemName, descWidth);
        const quantity = padRight(cart?.quantity ?? '', qtyWidth);
        const price =
            receiptType === 'customer'
                ? formatCurrency(
                      cart?.tax_rate && cart?.tax_rate > 0
                          ? Number(cart?.total_price || 0) - Number(cart?.tax_amount || 0)
                          : cart?.total_price,
                      generalSetting
                  )
                : '';

        descriptionLines.forEach((line, lineIndex) => {
            if (receiptType === 'customer') {
                rows.push(
                    `${lineIndex === 0 ? quantity : repeatChar(' ', qtyWidth)}${padRight(
                        line,
                        descWidth
                    )} ${lineIndex === 0 ? padLeft(price, priceWidth) : repeatChar(' ', priceWidth)}\n`
                );
            } else {
                rows.push(
                    `${lineIndex === 0 ? quantity : repeatChar(' ', qtyWidth)}${padRight(
                        line,
                        descWidth
                    )}\n`
                );
            }
        });

        getItemVariationsText(cart?.item_variations).forEach((variation) => {
            wrapText(variation, width - qtyWidth).forEach((line) => {
                rows.push(`${repeatChar(' ', qtyWidth)}${line}\n`);
            });
        });

        getItemExtrasText(cart?.item_extras).forEach((extra) => {
            wrapText(extra, width - qtyWidth).forEach((line) => {
                rows.push(`${repeatChar(' ', qtyWidth)}${line}\n`);
            });
        });

        if (cart?.instruction) {
            wrapText(`Instruction: ${cart.instruction}`, width - qtyWidth).forEach((line) => {
                rows.push(`${repeatChar(' ', qtyWidth)}${line}\n`);
            });
        }

        if (receiptType === 'customer' && cart?.tax_rate && Number(cart.tax_rate) > 0) {
            rows.push(
                buildKeyValueLine(
                    `${sanitizeText(cart?.tax_name || 'Tax')} (${cart?.tax_rate}%)`,
                    formatCurrency(cart?.tax_amount, generalSetting),
                    width
                )
            );
        }

        rows.push('\n');
    });

    return rows.join('');
};

const buildCustomerReceipt = ({ orderData, generalSetting, userData, width }) => {
    const buffer = [];

    buffer.push(COMMANDS.alignCenter, COMMANDS.boldOn, COMMANDS.largeSize);
    pushCenteredBlock(buffer, generalSetting?.company_name || 'Receipt', width);
    buffer.push(COMMANDS.normalSize, COMMANDS.boldOff);

    if (generalSetting?.company_address) {
        pushCenteredBlock(buffer, generalSetting.company_address, width);
    }
    if (generalSetting?.company_phone) {
        pushCenteredBlock(buffer, `Tel: ${generalSetting.company_phone}`, width);
    }
    if (generalSetting?.company_phone_2) {
        pushCenteredBlock(buffer, `Tel: ${generalSetting.company_phone_2}`, width);
    }

    buffer.push(COMMANDS.alignLeft, '\n');

    if (orderData?.order_serial_no) {
        buffer.push(`Order #${sanitizeText(orderData.order_serial_no)}\n`);
    }
    if (orderData?.order_datetime) {
        buffer.push(`Date: ${moment(orderData.order_datetime).format('Y-M-D HH:mm:ss')}\n`);
    }

    buffer.push(buildDivider(width));
    buffer.push(buildItemRows({ orderData, generalSetting, receiptType: 'customer', width }));
    buffer.push(buildDivider(width));

    if (Number(orderData?.total_tax) > 0) {
        buffer.push(
            buildKeyValueLine(
                'Subtotal:',
                formatCurrency(orderData?.withouttaxsubtotal ?? orderData?.subtotal, generalSetting),
                width
            )
        );
        buffer.push(
            buildKeyValueLine(
                'Total VAT:',
                formatCurrency(orderData?.total_tax, generalSetting),
                width
            )
        );
    } else {
        buffer.push(
            buildKeyValueLine(
                'Subtotal:',
                formatCurrency(orderData?.subtotal, generalSetting),
                width
            )
        );
    }

    buffer.push(
        buildKeyValueLine(
            'Discount:',
            formatCurrency(orderData?.discount || 0, generalSetting),
            width
        )
    );
    buffer.push(COMMANDS.boldOn);
    buffer.push(
        buildKeyValueLine('TOTAL:', formatCurrency(orderData?.total, generalSetting), width)
    );
    buffer.push(COMMANDS.boldOff, buildDivider(width));

    pushWrappedBlock(
        buffer,
        `Payment Type: ${
            orderData?.payment_method_id && orderData?.payment_gateway?.name
                ? orderData.payment_gateway.name
                : 'Cash'
        }`,
        width
    );
    pushWrappedBlock(
        buffer,
        `Payment Status: ${
            PAYMENT_STATUS_LABELS[Number(orderData?.payment_status)] || 'Unknown'
        }`,
        width
    );

    buffer.push(buildDivider(width), COMMANDS.alignCenter, COMMANDS.boldOn);
    pushCenteredBlock(buffer, `Served By: ${getServedBy(orderData, userData)}`, width);
    buffer.push(COMMANDS.boldOff);

    if (orderData?.token) {
        buffer.push(COMMANDS.largeSize, COMMANDS.boldOn);
        pushCenteredBlock(buffer, `Token #${sanitizeText(orderData.token)}`, width);
        buffer.push(COMMANDS.normalSize, COMMANDS.boldOff);
    }

    buffer.push('\n');
    pushCenteredBlock(buffer, 'Thank You', width);
    pushCenteredBlock(buffer, 'Please Come Again', width);
    buffer.push('\n');
    pushCenteredBlock(buffer, 'Powered by TabanFood', width);
    buffer.push(COMMANDS.alignLeft);

    return buffer.join('');
};

const buildKitchenReceipt = ({ orderData, userData, width }) => {
    const buffer = [];

    buffer.push(COMMANDS.alignCenter, COMMANDS.boldOn, COMMANDS.largeSize);
    pushCenteredBlock(buffer, 'Kitchen Receipt', width);
    buffer.push(COMMANDS.normalSize, COMMANDS.boldOff, COMMANDS.alignLeft, '\n');

    if (orderData?.order_datetime) {
        buffer.push(`Date: ${moment(orderData.order_datetime).format('Y-M-D HH:mm:ss')}\n`);
    }

    buffer.push(buildDivider(width));
    buffer.push(buildItemRows({ orderData, receiptType: 'kitchen', width }));
    buffer.push(buildDivider(width), COMMANDS.alignCenter, COMMANDS.boldOn);
    pushCenteredBlock(buffer, `Served By: ${getServedBy(orderData, userData)}`, width);
    buffer.push(COMMANDS.boldOff, COMMANDS.alignLeft);

    return buffer.join('');
};

const buildReceiptPayload = ({
    orderData,
    generalSetting,
    userData,
    receiptType,
    copyCount,
}) => {
    const width = getCharsPerLine(generalSetting);
    const safeCopies = Math.min(Math.max(Number(copyCount) || 1, 1), 10);
    const document = [];

    for (let index = 0; index < safeCopies; index += 1) {
        document.push(COMMANDS.init);
        document.push(
            receiptType === 'kitchen'
                ? buildKitchenReceipt({ orderData, userData, width })
                : buildCustomerReceipt({ orderData, generalSetting, userData, width })
        );
        document.push('\n\n\n');
        document.push(COMMANDS.cut);
    }

    return document.join('');
};

const ensureQzSecurity = () => {
    if (qzSecurityConfigured || !qz?.security) {
        return;
    }

    qz.security.setCertificatePromise((resolve) => resolve(''));
    qz.security.setSignaturePromise(() => (resolve) => resolve(''));
    qzSecurityConfigured = true;
};

const ensureQzConnection = async () => {
    ensureQzSecurity();

    if (qz?.websocket?.isActive?.()) {
        return;
    }

    try {
        await qz.websocket.connect({ retries: 0, delay: 0 });
    } catch (error) {
        throw new Error(
            'QZ Tray is not connected. Please install/open QZ Tray on this computer, then allow TabanFood to print.'
        );
    }
};

const resolvePrinterName = async (printerName) => {
    const trimmedPrinterName = sanitizeText(printerName);

    if (trimmedPrinterName) {
        try {
            return await qz.printers.find(trimmedPrinterName);
        } catch (error) {
            throw new Error(`Printer "${trimmedPrinterName}" was not found in QZ Tray.`);
        }
    }

    const defaultPrinter = await qz.printers.getDefault();
    if (!defaultPrinter) {
        throw new Error('No default printer is available for QZ Tray.');
    }

    return defaultPrinter;
};

export const resolveKitchenThermalPrinterName = async (generalSetting = {}) => {
    if (!qz) {
        return null;
    }

    const configuredPrinterName = getThermalPrinterName(generalSetting);
    if (configuredPrinterName) {
        return configuredPrinterName;
    }

    await ensureQzConnection();

    const defaultPrinter = await qz.printers.getDefault();
    return looksLikeReceiptPrinter(defaultPrinter) ? defaultPrinter : null;
};

export const isThermalReceiptPrintingEnabled = (generalSetting = {}) =>
    String(generalSetting?.receipt_print_method || DEFAULT_RECEIPT_PRINT_METHOD) === 'qz';

export const getThermalPrinterName = (generalSetting = {}) =>
    sanitizeText(generalSetting?.thermal_printer_name);

export const printThermalOrderReceipt = async ({
    orderData,
    generalSetting,
    userData,
    receiptType = 'customer',
    copyCount = 1,
    printerName,
}) => {
    if (!orderData) {
        throw new Error('Receipt data is still loading. Please try again.');
    }

    if (!qz) {
        throw new Error('QZ Tray library could not be loaded in the browser.');
    }

    await ensureQzConnection();

    const resolvedPrinterName = await resolvePrinterName(
        printerName || getThermalPrinterName(generalSetting)
    );
    const payload = buildReceiptPayload({
        orderData,
        generalSetting,
        userData,
        receiptType,
        copyCount,
    });

    const config = qz.configs.create(resolvedPrinterName, {
        encoding: 'UTF-8',
        forceRaw: true,
        jobName: `TabanFood ${receiptType === 'kitchen' ? 'Kitchen' : 'Customer'} Receipt`,
    });

    await qz.print(config, [
        {
            type: 'raw',
            format: 'command',
            flavor: 'plain',
            data: payload,
        },
    ]);
};
